import { FC, useEffect, useRef, useState } from 'react'
import { Divider } from 'antd'
import { useTranslation } from 'react-i18next'
import { NavLink, useLocation } from 'react-router-dom'
import { externalRoutes, TelegramIcon } from '../ExternalNavigation/ExternalNavigation'
import { routes } from '../Navigation/Navigation'
import style from './style.m.less'
import LanguageMenu from '../LanguageMenu/LanguageMenu'
import { AnimatedMenuIcon, Export } from '../../../../assets/icons'

const MobileGuestMenu: FC = () => {
    const { t } = useTranslation()
    const location = useLocation()
    const [isOpen, setIsOpen] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)

    const toggleOverlay = (state?: boolean) => {
        if (state === undefined) {
            setIsOpen((prev) => !prev)
        } else {
            setIsOpen(state)
        }
    }

    useEffect(() => {
        if (!isOpen) {
            return undefined
        }

        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            const target = event.target

            if (!(target instanceof Node)) {
                return
            }

            if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) {
                return
            }

            setIsOpen(false)
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('touchstart', handleClickOutside)

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('touchstart', handleClickOutside)
        }
    }, [isOpen])

    useEffect(() => {
        setIsOpen(false)
    }, [location.pathname, location.search, location.hash])

    return (
        <div className={style.menuWrap}>
            <button
                ref={buttonRef}
                className={style.button}
                type="button"
                aria-label="Open menu"
                onClick={() => toggleOverlay()}
            >
                <AnimatedMenuIcon state={isOpen} />
            </button>
            <div ref={menuRef} className={isOpen ? `${style.menu} ${style.active}` : style.menu}>
                {routes.map((route) => (
                    <div className={style.menuItem} key={route.id}>
                        <NavLink
                            className={({ isActive }) =>
                                isActive ||
                                (route.to === 'main' && location.pathname === '/') ||
                                route.addUrls?.some((url: string) =>
                                    location.pathname.includes(url),
                                )
                                    ? style.menuLinkActive
                                    : ''
                            }
                            to={route.to}
                            state={route.state}
                            onClick={() => setIsOpen(false)}
                        >
                            {t(route.title)}
                        </NavLink>
                    </div>
                ))}
                <Divider type="horizontal" style={{ marginBottom: 1 }} />
                <Divider type="horizontal" style={{ marginTop: 0 }} />
                {externalRoutes.map((route) => (
                    <div className={style.menuItem}>
                        <a
                            href={route.href}
                            target="_blank"
                            className={route.isGreen ? style.green : undefined}
                            rel="noreferrer"
                        >
                            {t(route.title)} <Export />
                        </a>
                    </div>
                ))}
                <Divider type="horizontal" />
                <div className={style.menuItemAuth}>
                    <NavLink
                        to="/auth"
                        className={({ isActive }) => (isActive ? style.menuLinkActive : '')}
                        onClick={() => setIsOpen(false)}
                    >
                        {t('header.signIn')}
                    </NavLink>
                </div>
                <Divider type="horizontal" style={{ marginBottom: 12 }} />
                <div key="telegram">
                    <div className={style.telegramItem}>
                        <LanguageMenu />
                        <a
                            href="https://t.me/green_e_track"
                            target="_blank"
                            className={style.telegramButton}
                            rel="noreferrer"
                            aria-label="Telegram"
                        >
                            <TelegramIcon />
                        </a>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default MobileGuestMenu
